import { Button, Checkbox, FormControlLabel } from '@mui/material';
import CenterLoader from 'components/CenterLoader';
import CustomBadge from 'components/CustomBadge';
import { PropertyPagination } from 'components/Pagination';
import { useLeads, usePrefetchLeads } from 'hooks/useLeads';
import { useModals } from 'pages/MainProvider';
import { useState } from 'react';
import { useLeadsStore } from 'store/leadsStore';
import { Lead } from 'types/Lead';
import { formatDate } from 'util/formatDate';
import LeadModal from './LeadModal';

export const LeadsContent = () => {
    const [open, setOpen] = useState(false);
    const [openLeadId, setOpenLeadId] = useState<string | null>(null);
    const { currentPage, setCurrentPage, status } = useLeadsStore();
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
    const { showPropertyDetailModal } = useModals();

    const { data, isLoading } = useLeads(currentPage, status);
    usePrefetchLeads();
    const inquiries = data!?.inquiries!;
    const total = data!?.total!;

    const totalPageCount = Math.ceil(total / 15);

    const handleOpen = (leadId: string) => setOpenLeadId((prev) => (prev === leadId ? null : leadId));

    if (isLoading) return <CenterLoader />;

    return (
        <div className="w-full font-medium">
            {/* Small screen header */}
            <div className="lg:hidden md:hidden grid grid-cols-2 items-center text-my-black text-opacity-60 border-y border-my-gray border-opacity-40 py-[15px] px-[10px] mb-[12px] bg-[#DADADA]/20 rounded-t-2xl">
                <div className="flex items-center gap-[4px]">
                    <FormControlLabel label="" control={<Checkbox />} />
                    <span>Name</span>
                </div>
            </div>

            {/* Medium screen header */}
            <div className="hidden md:grid lg:hidden grid-cols-4 gap-4 items-center text-my-black text-opacity-60 border-y border-my-gray border-opacity-40 py-[15px] px-[10px] mb-[12px] bg-[#DADADA]/20 rounded-t-2xl">
                <div className="flex items-center gap-[4px]">
                    <FormControlLabel label="" control={<Checkbox />} />
                    <span>Date</span>
                </div>
                <span>Name</span>
                <span>Email</span>
                <div className="flex justify-between">
                    <span>Status</span>
                </div>
            </div>

            {/* Large screen header */}
            <div className="hidden lg:grid grid-cols-8 gap-24 items-center text-my-black text-opacity-60 border-y border-my-gray border-opacity-40 py-[15px] px-[10px] mb-[12px] bg-[#DADADA]/20 rounded-t-2xl">
                <div className="flex items-center gap-[4px]">
                    <FormControlLabel label="" control={<Checkbox />} />
                    <span>Date</span>
                </div>
                <span>Name</span>
                <span>Email</span>
                <span>Phone Number</span>
                <span className="col-span-2">Listing</span>
                <span>Status</span>
            </div>

            {/* Responsive body */}
            <div className="flex flex-col gap-[4px] text-opacity-80">
                {inquiries && inquiries?.length > 0 ? (
                    inquiries.map((lead, index) => (
                        <div key={index} className={`${index % 2 === 1 ? 'bg-gray-50' : ''} p-4 rounded-lg`}>
                            {/* Small screens - Only checkbox, name, and details button */}
                            <div className="flex items-center justify-between lg:hidden md:hidden">
                                <div className="flex items-center gap-2">
                                    <FormControlLabel label="" control={<Checkbox />} />
                                    <span className="font-medium">{lead.name}</span>
                                </div>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setSelectedLead(lead);
                                        setOpen(true);
                                    }}
                                >
                                    Details
                                </Button>
                            </div>

                            {/* Medium screens - More info but not full */}
                            <div className="hidden md:grid lg:hidden grid-cols-4 gap-4 items-center">
                                <div className="flex items-center gap-2">
                                    <FormControlLabel label="" control={<Checkbox />} />
                                    <span>{formatDate(lead.createdAt)}</span>
                                </div>
                                <span>{lead.name}</span>
                                <span>{lead.email}</span>
                                <div className="flex items-center justify-between">
                                    <CustomBadge status={lead.status} leadId={lead.id} isOpen={openLeadId === lead.id} onToggle={() => handleOpen(lead.id)} />
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedLead(lead);
                                            setOpen(true);
                                        }}
                                    >
                                        Details
                                    </Button>
                                </div>
                            </div>

                            {/* Large screens - Full information */}
                            <div className="hidden lg:grid grid-cols-8 gap-24 items-center">
                                <div className="flex items-center gap-[4px]">
                                    <FormControlLabel label="" control={<Checkbox />} />
                                    <span>{formatDate(lead.createdAt)}</span>
                                </div>
                                <span>{lead.name}</span>
                                <span>{lead.email}</span>
                                <span className="ml-4">{lead.phone}</span>
                                <div className="col-span-2 flex items-center">
                                    <div className="flex-1 min-w-0">
                                        <span className="block truncate text-my-black">{lead.property.title}</span>
                                    </div>
                                    <button className="text-blue-500 whitespace-nowrap ml-1" onClick={() => showPropertyDetailModal(lead.property, false)}>
                                        (View)
                                    </button>
                                </div>
                                <CustomBadge status={lead.status} leadId={lead.id} isOpen={openLeadId === lead.id} onToggle={() => handleOpen(lead.id)} />
                                <div className="flex items-center">
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedLead(lead);
                                            setOpen(true);
                                        }}
                                    >
                                        Details
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex justify-center items-center h-[200px] font-semibold text-xlg">
                        <span className="text-center">No Leads Available</span>
                    </div>
                )}
            </div>

            {total > 1 && (
                <div className="mx-auto w-fit pt-[10px]">
                    <PropertyPagination handlePageChange={setCurrentPage} totalPageCount={totalPageCount} currentPage={currentPage} />
                </div>
            )}
            {selectedLead && <LeadModal visible={open} onClose={() => setOpen(false)} lead={selectedLead!} />}
        </div>
    );
};
